"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { FileText } from "lucide-react";
import config from "../../config/config";
import SectionHeader from "./SectionHeader";
import ArticleCard from "../cards/ArticleCard";
import PostsSkeleton from "../posts/PostsSkeleton";

export default function ArticleSection() {
  const [articles, setArticles] = useState([]);
  const [articleSettings, setArticleSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch homepage settings for article section
        const settingsResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/article`);
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.data) {
          setArticleSettings(settingsData.data);

          // Only fetch articles if section is enabled
          if (settingsData.data.enabled !== false) {
            const articlesResponse = await fetch(
              `${config.NEXT_PUBLIC_API_URL}/api/public/p/blog?limit=${settingsData.data.limit ? settingsData.data.limit : 6}`
            );

            if (articlesResponse.ok) {
              const articlesData = await articlesResponse.json();
              if (articlesData.success) {
                const articles = articlesData.data.posts ? articlesData.data.posts : [];
                setArticles(articles);
              }
            }
          }
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };



  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto w-full">
          <SectionHeader
            icon={FileText}
            sectionTitle="Articles"
            mainHeading="Latest Articles"
          />
          <PostsSkeleton />
        </div>
      </section>
    );
  }

  // Hide section if disabled or no meaningful content
  if (articleSettings.enabled === false) {
    return null;
  }
  if (!getLabel(articleSettings.sectionTitle) && !getLabel(articleSettings.sectionSubtitle) && articles.length === 0) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ background: 'linear-gradient(135deg, var(--color-muted) 0%, var(--color-primary)/5% 50%, var(--color-muted) 100%)' }}>
      <div className="max-w-7xl mx-auto w-full">
        <SectionHeader
          icon={FileText}
          sectionTitle={articleSettings.sectionTitle}
          mainHeading={articleSettings.sectionSubtitle}
          description={articleSettings.description}
          buttonText={articleSettings.viewAllButtonText}
          buttonLink={articles.length > 0 && articleSettings.showViewAllButton !== false ? (articleSettings.viewAllButtonLink || '/articles') : null}
          onButtonClick={articles.length > 0 && articleSettings.showViewAllButton !== false ? () => {
            const link = articleSettings.viewAllButtonLink ? articleSettings.viewAllButtonLink : '/articles';
            const target = articleSettings.viewAllButtonTarget ? articleSettings.viewAllButtonTarget : '_self';
            if (target === '_blank') {
              window.open(link, '_blank');
            } else {
              window.location.href = link;
            }
          } : null}
          getLabel={getLabel}
        />

        {/* Articles Grid */}
        <div className="mobile-horizontal-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {articles.map((article, index) => (
            <ArticleCard key={article._id || article.id} article={article} />
          ))}
        </div>


      </div>
    </section>
  );
}